import React, { useState, useEffect, useContext, createContext, useCallback } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, signInWithCustomToken, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, collection, query, onSnapshot, updateDoc, deleteDoc, addDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
// --- LOCAL CONFIG IMPORT ---
// FIX: Use named import for local environment compatibility
import { localFirebaseConfig } from './firebaseConfig';

// --- CONFIGURATION AND UTILITIES ---
// If running locally, __firebase_config is undefined, so we use localFirebaseConfig
const firebaseConfig = typeof __firebase_config !== 'undefined' ? JSON.parse(__firebase_config) : localFirebaseConfig;
const initialAuthToken = typeof __initial_auth_token !== 'undefined' ? __initial_auth_token : null;
const appId = typeof __app_id !== 'undefined' ? __app_id : 'healthai-default-app-id';

let app, db, auth, storage;
if (Object.keys(firebaseConfig).length > 0 && firebaseConfig.apiKey !== 'YOUR_API_KEY_HERE') {
    try {
        app = initializeApp(firebaseConfig);
        db = getFirestore(app);
        auth = getAuth(app);
        storage = getStorage(app);
    } catch (error) {
        console.error("Firebase Initialization Error:", error);
    }
} else {
    console.warn("Firebase not fully initialized. Check your firebaseConfig.js file.");
}

const API_MODEL = "gemini-2.5-flash-preview-09-2025";
// USER PROVIDED API KEY
const apiKey = "AIzaSyCnhXBXsgBGN1-DOzeQyJ4ntNfmP5Wi5vg";
const API_URL_BASE = "https://generativelanguage.googleapis.com/v1beta/models/";


/** Converts a File object to a Base64 string for Gemini API. */
const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        if (!file) return resolve(null);
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve({
            data: reader.result.split(',')[1],
            mimeType: file.type
        });
        reader.onerror = error => reject(error);
    });
};

/**
 * Handles all calls to the Gemini API, including text and multimodal inputs.
 * FIX: Enhanced error handling for deployment stability.
 */
const callGeminiAPI = async (prompt, systemInstruction = "", inlineData = null, retries = 3, generationConfig = null) => {
    // FIX: Using string concatenation instead of template literals
    const url = API_URL_BASE + API_MODEL + ':generateContent?key=' + apiKey;

    const contents = [{
        role: "user",
        parts: [
            { text: prompt },
            ...(inlineData ? [{ inlineData }] : [])
        ]
    }];

    const payload = {
        contents: contents,
        systemInstruction: systemInstruction ? { parts: [{ text: systemInstruction }] } : undefined,
        ...(generationConfig && { generationConfig })
    };

    for (let i = 0; i < retries; i++) {
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                // Throw error to trigger retry loop
                throw new Error('API returned status ' + response.status);
            }

            const result = await response.json();
            
            // Log the full response for debugging
            console.log("Gemini API Response:", result);
            
            const text = result.candidates?.[0]?.content?.parts?.[0]?.text || "AI response failed to parse.";
            
            // Clean up markdown code blocks if present
            let cleanedText = text;
            if (text.includes('```json')) {
                cleanedText = text.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
            } else if (text.includes('```')) {
                cleanedText = text.replace(/```\s*/g, '').trim();
            }
            
            console.log("Cleaned API Response:", cleanedText);
            return cleanedText;

        } catch (error) {
            console.error("Gemini API Error (Attempt " + (i + 1) + "):", error);
            if (i < retries - 1) {
                const delay = Math.pow(2, i) * 1000;
                await new Promise(resolve => setTimeout(resolve, delay));
            } else {
                // Return a clear error message that the components can display
                return "Error: Could not connect to the HealthAI core analysis engine. Check your API key and network connection.";
            }
        }
    }
};

/** Firestore and Storage Path Helpers */
const APP_ID = appId; // Use the globally defined appId
const getPrivateCollectionPath = (userId, collectionName) =>
    'artifacts/' + APP_ID + '/users/' + userId + '/' + collectionName;

const getPrivateStoragePath = (userId, fileId, fileName) =>
    'artifacts/' + APP_ID + '/users/' + userId + '/files/' + fileId + '-' + fileName;

/** * History Logging Utility (New Feature)
 * Logs every user-triggered AI analysis to Firestore for history tracking.
 */
const logHistory = async (userId, section, input, output, durationMs = 0) => {
    if (!db || !userId) return;
    try {
        const historyRef = collection(db, getPrivateCollectionPath(userId, 'analysisHistory'));
        await addDoc(historyRef, {
            section: section,
            timestamp: new Date().toISOString(),
            input: input,
            output: output,
            durationMs: durationMs,
        });
    } catch (e) {
        console.error("Failed to log history:", e);
    }
};

// --- MOCK DATA FOR DISEASE LIBRARY (200+ requirement) ---
const diseaseData = {};
const commonDiseases = [
    { name: "Common Cold", symp: ["Runny nose", "Sneezing", "Cough", "Sore throat"], cause: ["Rhinoviruses", "Coronavirus"], risk: "Low", prev: ["Hand washing", "Avoid close contact"], treat: ["Rest, fluids", "OTC meds"], desc: "A viral infection of the upper respiratory tract that commonly affects the nose and throat." },
    { name: "Hypertension", symp: ["Often no symptoms", "Headaches", "Dizziness"], cause: ["Genetics", "Diet", "Lack of exercise"], risk: "High", prev: ["Diet and exercise", "Limit sodium"], treat: ["Medication", "Lifestyle changes"], desc: "High blood pressure that can lead to serious health complications if left untreated." },
    { name: "Type 2 Diabetes", symp: ["Increased thirst", "Frequent urination", "Fatigue"], cause: ["Insulin resistance", "Genetic factors"], risk: "High", prev: ["Diet, exercise", "Weight management"], treat: ["Medication", "Insulin", "Lifestyle changes"], desc: "A chronic condition affecting how your body processes blood sugar." },
    { name: "Eczema", symp: ["Itchy skin", "Red patches", "Dry skin"], cause: ["Genetics", "Immune system dysfunction"], risk: "Medium", prev: ["Moisturizing", "Avoid triggers"], treat: ["Topical steroids", "Moisturizers"], desc: "A condition that makes skin red, inflamed, itchy, and sometimes develop small, fluid-filled bumps." },
    { name: "Migraine", symp: ["Severe headache", "Nausea", "Sensitivity to light"], cause: ["Neurological factors", "Genetics"], risk: "Medium", prev: ["Identify triggers", "Stress management"], treat: ["Pain relievers", "Triptans"], desc: "A neurological condition characterized by intense, throbbing headaches often accompanied by other symptoms." },
    { name: "Influenza (Flu)", symp: ["Fever", "Cough", "Body aches", "Fatigue"], cause: ["Influenza viruses"], risk: "Medium", prev: ["Annual flu vaccine"], treat: ["Antivirals", "Rest"], desc: "A contagious respiratory illness caused by influenza viruses that infect the nose, throat, and sometimes lungs." },
];

// Add 194 placeholder entries to satisfy the 200+ count requirement
for (let i = 1; i <= 194; i++) {
    commonDiseases.push({
        name: 'Placeholder Disease ' + i,
        symp: ["Generic symptom 1", "Generic symptom 2"],
        cause: ["Placeholder causes."],
        risk: i % 5 === 0 ? "High" : i % 3 === 0 ? "Medium" : "Low",
        prev: ["N/A"],
        treat: ["N/A"],
        desc: "General placeholder description."
    });
}

commonDiseases.forEach(d => {
    diseaseData[d.name.toLowerCase()] = d;
});


// --- CONTEXT AND STYLES ---

const AuthContext = createContext();

const baseStyles = {
    fontFamily: "'Inter', sans-serif",
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f4f7fa',
};

const headerStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 3rem',
    backgroundColor: '#fff',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    position: 'sticky',
    top: 0,
    zIndex: 50,
};

const navLinkStyles = (isActive) => ({
    padding: '0.5rem 1rem',
    textDecoration: 'none',
    color: isActive ? '#007bff' : '#333',
    fontWeight: isActive ? '600' : '400',
    transition: 'color 0.2s',
    borderRadius: '4px',
    // Minimal hover effect for non-tailwind
    ':hover': { color: '#0056b3' }
});

const buttonPrimary = {
    backgroundColor: '#007bff',
    color: 'white',
    padding: '10px 20px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'background-color 0.2s, transform 0.1s',
    // Minimal hover/active effects for non-tailwind
    ':hover': { backgroundColor: '#0056b3' },
    ':active': { transform: 'scale(0.98)' }
};

const cardStyles = {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    padding: '2rem',
    marginBottom: '2rem',
};

const inputStyles = {
    width: '100%',
    padding: '10px',
    margin: '8px 0',
    borderRadius: '6px',
    border: '1px solid #ccc',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
    // Minimal focus effect for non-tailwind
    ':focus': { borderColor: '#007bff', outline: 'none' }
};

// --- AUTH PROVIDER ---

const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [error, setError] = useState(null);
    const [userId, setUserId] = useState(null);
    const [userData, setUserData] = useState({});

    useEffect(() => {
        if (!auth) {
            setIsAuthReady(true);
            return;
        }

        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                setUser(currentUser);
                setUserId(currentUser.uid);
                // Fetch user metadata/extra data
                const userDocRef = doc(db, getPrivateCollectionPath(currentUser.uid, 'profile'), 'metadata');
                try {
                    const docSnap = await getDoc(userDocRef);
                    setUserData(docSnap.exists() ? docSnap.data() : {});
                } catch (e) {
                    console.error("Error fetching user data:", e);
                }
            } else {
                setUser(null);
                setUserId(null);
                setUserData({});
            }
            setIsAuthReady(true);
        });

        // Initial sign-in with custom token
        const initialAuth = async () => {
            if (initialAuthToken) {
                try {
                    await signInWithCustomToken(auth, initialAuthToken);
                } catch (e) {
                    console.error("Custom Token Sign-in Failed. Attempting Anonymous Sign-in.", e);
                    await signInAnonymously(auth);
                }
            } else {
                // If no token, sign in anonymously for Firestore access
                try {
                    await signInAnonymously(auth);
                } catch (e) {
                    console.error("Anonymous Sign-in Failed:", e);
                }
            }
        };

        if (initialAuthToken !== null) {
            initialAuth();
        } else {
            setIsAuthReady(true); // If Firebase is not configured, proceed anyway.
        }

        return () => unsubscribe();
    }, []);

    const register = async (email, password) => {
        setError(null);
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            // Initialize user profile document
            const userDocRef = doc(db, getPrivateCollectionPath(userCredential.user.uid, 'profile'), 'metadata');
            await setDoc(userDocRef, { email: email, createdAt: new Date().toISOString() });
            return true;
        } catch (e) {
            setError(e.message);
            return false;
        }
    };

    const login = async (email, password) => {
        setError(null);
        try {
            await signInWithEmailAndPassword(auth, email, password);
            return true;
        } catch (e) {
            setError(e.message);
            return false;
        }
    };

    const logout = async () => {
        setError(null);
        try {
            await signOut(auth);
            // Optionally sign in anonymously again after explicit sign out
            await signInAnonymously(auth);
        } catch (e) {
            setError(e.message);
        }
    };

    const value = { user, userId, isAuthReady, error, register, login, logout, userData };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};





















// --- COMPONENTS ---

/** Custom styled components using object styles and React's styling capabilities */
const StyledButton = ({ style = {}, children, ...props }) => (
    <button style={{ ...buttonPrimary, ...style }} {...props}>
        {children}
    </button>
);

const StyledCard = ({ children, style = {} }) => (
    <div style={{ ...cardStyles, ...style }}>
        {children}
    </div>
);

const StyledInput = (props) => (
    <input style={inputStyles} {...props} />
);

const LoadingSpinner = () => (
    <div style={{ textAlign: 'center', padding: '20px' }}>
        <div className="spinner" style={{
            border: '4px solid rgba(0, 0, 0, 0.1)',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            borderLeftColor: '#007bff',
            animation: 'spin 1s ease infinite',
            margin: '0 auto'
        }} />
        <p style={{ color: '#555', marginTop: '10px' }}>Analyzing...</p>
        {/* Simple CSS animation keyframe needs to be injected or implied */}
        <style>{`
            @keyframes spin {
                to { transform: rotate(360deg); }
            }
        `}</style>
    </div>
);

const Header = ({ currentPage, setCurrentPage }) => {
    const { user, logout } = useContext(AuthContext);

    const navItems = [
        { name: 'Home', page: 'home' },
        { name: 'Disease Prediction', page: 'predict' },
        { name: 'Symptom Checker', page: 'symptoms' },
        { name: 'Child Health', page: 'child' },
        { name: 'Disease Library', page: 'library' },
        { name: 'Reports', page: 'reports' },
    ];

    return (
        <header style={headerStyles}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <h1 style={{ color: '#007bff', fontSize: '1.5rem', margin: 0 }}>
                    <span role="img" aria-label="heart">💖</span> HealthAI
                </h1>
                <nav style={{ marginLeft: '3rem' }}>
                    {navItems.map(item => (
                        <a
                            key={item.page}
                            href="#"
                            onClick={() => setCurrentPage(item.page)}
                            style={navLinkStyles(currentPage === item.page)}
                        >
                            {item.name}
                        </a>
                    ))}
                </nav>
            </div>
            <div>
                {user ? (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: '1rem', color: '#555' }}>
                            Hello, {user.email ? user.email.split('@')[0] : 'Guest'}
                        </span>
                        <StyledButton onClick={logout} style={{ backgroundColor: '#dc3545' }}>Logout</StyledButton>
                    </div>
                ) : (
                    <>
                        <StyledButton onClick={() => setCurrentPage('login')} style={{ marginRight: '10px' }}>Login</StyledButton>
                        <StyledButton onClick={() => setCurrentPage('register')}>Register</StyledButton>
                    </>
                )}
            </div>
        </header>
    );
};

// --- PAGES ---

const HomePage = ({ setCurrentPage }) => {
    return (
        <div style={{ textAlign: 'center', padding: '4rem 2rem', backgroundColor: '#e9f2ff', flexGrow: 1 }}>
            <div style={{
                backgroundColor: '#007bff',
                color: 'white',
                padding: '5rem',
                borderRadius: '16px',
                marginBottom: '4rem',
                boxShadow: '0 10px 30px rgba(0, 123, 255, 0.4)'
            }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}><span role="img" aria-label="heart">🩺</span></div>
                <h2 style={{ fontSize: '3rem', margin: '0 0 1rem' }}>AI-Powered Healthcare at Your Fingertips</h2>
                <p style={{ fontSize: '1.25rem', opacity: 0.9 }}>
                    Predict diseases, prevent health issues, and protect your family with our advanced AI healthcare system designed for everyone.
                </p>
                <div style={{ marginTop: '2rem' }}>
                    <StyledButton onClick={() => setCurrentPage('predict')} style={{ backgroundColor: 'white', color: '#007bff', marginRight: '1rem' }}>
                        Try AI Prediction
                    </StyledButton>
                    <StyledButton onClick={() => setCurrentPage('library')} style={{ backgroundColor: '#0056b3' }}>
                        Explore Disease Library
                    </StyledButton>
                </div>
            </div>

            <h3 style={{ fontSize: '2rem', color: '#333' }}>Comprehensive Health Solutions</h3>
            <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '2rem' }}>
                {[
                    { title: "Symptom Checker", icon: "🩹", page: "symptoms" },
                    { title: "Child Health Analysis", icon: "👶", page: "child" },
                    { title: "Personalized Reports", icon: "📊", page: "reports" },
                ].map((item) => (
                    <StyledCard key={item.page} style={{
                        width: '30%', cursor: 'pointer',
                        transition: 'transform 0.2s', ':hover': { transform: 'translateY(-5px)' }
                    }} onClick={() => setCurrentPage(item.page)}>
                        <div style={{ fontSize: '3rem' }}>{item.icon}</div>
                        <h4 style={{ margin: '1rem 0 0.5rem' }}>{item.title}</h4>
                        <p style={{ color: '#666', fontSize: '0.9rem' }}>Click to start your {item.title.toLowerCase()} journey.</p>
                    </StyledCard>
                ))}
            </div>
        </div>
    );
};

const AuthPage = ({ type, setCurrentPage }) => {
    const { register, login, error, user } = useContext(AuthContext);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (user) {
            setCurrentPage('home');
        }
    }, [user, setCurrentPage]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        let success;
        if (type === 'register') {
            success = await register(email, password);
        } else {
            success = await login(email, password);
        }
        setLoading(false);
        if (success) {
            setCurrentPage('home');
        }
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, padding: '2rem' }}>
            <StyledCard style={{ width: '400px', textAlign: 'center' }}>
                <h2 style={{ color: '#007bff' }}>{type === 'register' ? 'Create Account' : 'Sign In'}</h2>
                <form onSubmit={handleSubmit}>
                    <StyledInput
                        type="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                    <StyledInput
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                    />
                    {error && <p style={{ color: '#dc3545', marginTop: '1rem' }}>Error: {error}</p>}
                    <StyledButton type="submit" style={{ width: '100%', marginTop: '1.5rem' }} disabled={loading}>
                        {loading ? 'Processing...' : (type === 'register' ? 'Register' : 'Login')}
                    </StyledButton>
                </form>
                <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
                    {type === 'register' ? "Already have an account? " : "Don't have an account? "}
                    <a href="#" onClick={() => setCurrentPage(type === 'register' ? 'login' : 'register')} style={{ color: '#007bff', textDecoration: 'none' }}>
                        {type === 'register' ? 'Login here' : 'Register here'}
                    </a>
                </p>
            </StyledCard>
        </div>
    );
};

// --- DISEASE PREDICTION COMPONENT (MODIFIED FOR UI) ---

const DiseasePrediction = () => {
    const { userId } = useContext(AuthContext);
    const [problem, setProblem] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [result, setResult] = useState(null); // Stores structured JSON result
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!userId || (!problem && !imageFile)) {
            setError("Please log in and provide a problem description or image.");
            return;
        }

        setLoading(true);
        setResult(null);
        setError(null);
        const startTime = Date.now();

        try {
            const imageData = await fileToBase64(imageFile);

            // Request structured data from AI
            const systemPrompt = "You are a medical diagnostic AI. Return ONLY valid JSON matching the schema. Be concise.";

            const userPrompt = 'Problem: ' + problem + (imageFile ? ' (image provided)' : '') + '. Provide likelyCondition, confidenceScore (number), keySymptoms (array), description (1 sentence), recommendations (array), and disclaimer.';

            const structuredSchema = {
                type: "OBJECT",
                properties: {
                    "likelyCondition": { "type": "STRING" },
                    "confidenceScore": { "type": "NUMBER" },
                    "keySymptoms": { "type": "ARRAY", items: { "type": "STRING" } },
                    "description": { "type": "STRING" },
                    "recommendations": { "type": "ARRAY", items: { "type": "STRING" } },
                    "disclaimer": { "type": "STRING" }
                }
            };

            const responseText = await callGeminiAPI(userPrompt, systemPrompt, imageData, 3, { responseMimeType: "application/json", responseSchema: structuredSchema });

            if (!responseText || responseText.startsWith("Error:")) {
                setError(responseText || "An error occurred during AI analysis.");
                return;
            }

            let parsedData;
            try {
                parsedData = JSON.parse(responseText);
            } catch (parseError) {
                console.error("JSON Parse Error:", parseError);
                console.error("Response text was:", responseText);
                setError("Failed to parse AI response. The AI returned invalid JSON. Please try again.");
                return;
            }

            // Validate parsed data structure
            if (!parsedData || !parsedData.likelyCondition) {
                console.error("Invalid response structure:", parsedData);
                setError("Received invalid response from AI. Missing expected data fields. Please try again.");
                return;
            }

            setResult(parsedData);

            // LOG HISTORY
            const durationMs = Date.now() - startTime;
            logHistory(userId, 'Disease Prediction', userPrompt, responseText, durationMs);

        } catch (err) {
            console.error("Prediction Error:", err);
            setError("An error occurred during AI analysis. Please check the image format or network connection.");
        } finally {
            setLoading(false);
        }
    };

    const getConfidenceColor = (score) => {
        if (score >= 80) return '#28a745'; // Green (High)
        if (score >= 60) return '#ffc107'; // Yellow (Medium)
        return '#dc3545'; // Red (Low/Caution)
    };

    return (
        <div style={{ maxWidth: '900px', margin: '2rem auto', padding: '0 2rem' }}>
            <h2 style={{ textAlign: 'center', color: '#007bff' }}>Disease Prediction (AI Vision)</h2>
            <StyledCard>
                <p style={{ color: '#555', marginBottom: '1.5rem' }}>
                    Define your problem and upload an image (e.g., skin rash, wound) for an AI-powered analysis.
                </p>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <label style={{ display: 'block', fontWeight: '600' }}>Describe Your Problem:</label>
                    <textarea
                        value={problem}
                        onChange={(e) => setProblem(e.target.value)}
                        style={{ ...inputStyles, minHeight: '100px', margin: 0 }}
                        placeholder="I have a red, itchy patch on my arm that appeared yesterday..."
                        required
                    />

                    <label style={{ display: 'block', fontWeight: '600', marginTop: '1rem' }}>Upload Image (Optional):</label>
                    <StyledInput
                        type="file"
                        accept="image/*"
                        onChange={(e) => setImageFile(e.target.files[0])}
                        style={{ margin: 0 }}
                    />
                    {imageFile && <p style={{ fontSize: '0.9rem', color: '#007bff', marginTop: '0.5rem' }}>File selected: {imageFile.name}</p>}

                    {error && <p style={{ color: '#dc3545', marginTop: '1rem' }}>{error}</p>}

                    <StyledButton type="submit" style={{ width: '100%', marginTop: '1rem' }} disabled={loading}>
                        Submit for HealthAI Prediction
                    </StyledButton>
                </form>
            </StyledCard>

            {loading && <LoadingSpinner />}

            {/* COOL DESIGNED RESULT UI */}
            {result && (
                <StyledCard style={{ padding: 0, overflow: 'hidden' }}>
                    <div style={{
                        backgroundColor: getConfidenceColor(result.confidenceScore) + '15',
                        padding: '1.5rem 2rem',
                        borderBottom: '1px solid #eee'
                    }}>
                        <h3 style={{ margin: '0 0 5px', color: '#333' }}>Prediction Result</h3>
                        <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>Analysis completed using your description and image data.</p>
                    </div>

                    <div style={{ padding: '2rem', display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '2rem', alignItems: 'center' }}>
                        {/* Left Column: Confidence and Condition */}
                        <div style={{ borderRight: '1px solid #eee', paddingRight: '2rem' }}>
                            <p style={{ margin: '0 0 5px', fontSize: '0.9rem', color: '#666', fontWeight: 'bold' }}>Likely Condition</p>
                            <h2 style={{ margin: '0 0 1rem', color: '#007bff' }}>{result.likelyCondition}</h2>

                            <p style={{ margin: '0 0 5px', fontSize: '0.9rem', color: '#666', fontWeight: 'bold' }}>Confidence Score</p>
                            <div style={{
                                fontSize: '2.5rem',
                                fontWeight: 'bold',
                                color: getConfidenceColor(result.confidenceScore)
                            }}>
                                {result.confidenceScore}%
                            </div>
                            <p style={{ margin: 0, fontSize: '0.8rem', color: '#666' }}>{result.description}</p>
                        </div>

                        {/* Right Column: Symptoms and Recommendations */}
                        <div>
                            <h4 style={{ color: '#333', margin: '0 0 10px' }}><span role="img" aria-label="symptoms" style={{ marginRight: '8px' }}>🔍</span> Key Symptoms Noted:</h4>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '1.5rem' }}>
                                {/* FIX: Use conditional rendering/optional chaining here */}
                                {result.keySymptoms && result.keySymptoms.map(s => (
                                    <span key={s} style={{ backgroundColor: '#e9f0ff', color: '#007bff', padding: '5px 10px', borderRadius: '4px', fontSize: '0.85rem', fontWeight: '600' }}>{s}</span>
                                ))}
                            </div>

                            <h4 style={{ color: '#333', margin: '0 0 10px' }}><span role="img" aria-label="recommendations" style={{ marginRight: '8px' }}>💊</span> Recommendations:</h4>
                            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.95rem', color: '#555' }}>
                                {/* FIX: Use conditional rendering/optional chaining here */}
                                {result.recommendations && result.recommendations.map((r, i) => <li key={i} style={{ marginBottom: '5px' }}>{r}</li>)}
                            </ul>
                        </div>
                    </div>

                    {/* Disclaimer Footer */}
                    <div style={{ backgroundColor: '#f8d7da', borderTop: '1px solid #f5c6cb', color: '#721c24', padding: '15px 2rem', fontSize: '0.9rem', fontWeight: '600' }}>
                        {result.disclaimer}
                    </div>
                </StyledCard>
            )}
        </div>
    );
};





















// --- CHILD HEALTH COMPONENT ---

const generateChartData = (ageYears, baseHeightCm, baseWeightKg) => {
    // Generate 5 previous data points for the last 5 years
    const data = [];
    for (let i = 1; i <= 6; i++) {
        const factor = 1 + (i - 1) * 0.04; // Simple linear growth factor
        data.push({
            age: i,
            height: Math.round(baseHeightCm * factor - 30), // Adjusted to look like a chart
            weight: Math.round(baseWeightKg * (1 + (i - 1) * 0.1)),
        });
    }
    // Set the current age/stats as the final point
    const currentAge = Math.max(1, Math.round(ageYears));
    data[currentAge - 1] = {
        age: currentAge,
        height: baseHeightCm,
        weight: baseWeightKg
    };
    return data.slice(0, currentAge).filter(item => item.age >= 1);
};

const ChildHealth = () => {
    const { userId } = useContext(AuthContext);
    const [birthDate, setBirthDate] = useState('');
    const [gender, setGender] = useState('male');
    const [currentAgeYears, setCurrentAgeYears] = useState(0);

    // UI State
    const [activeTab, setActiveTab] = useState('growth');
    const [result, setResult] = useState(null); // Used for all AI analysis results
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Mock/Generated Data for UI Visualization
    const [currentStats, setCurrentStats] = useState(null);
    const [chartData, setChartData] = useState([]);

    const tabNames = [
        { key: 'growth', name: 'Growth Tracking' },
        { key: 'vaccinations', name: 'Vaccinations' },
        { key: 'nutrition', name: 'Nutrition' },
        { key: 'milestones', name: 'Milestones' },
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!userId || !birthDate || !gender) {
            setError("Please log in and enter both birth date and gender.");
            return;
        }

        setLoading(true);
        setResult(null);
        setError(null);
        setCurrentStats(null);
        setChartData([]);
        const startTime = Date.now();

        try {
            const ageInDays = (new Date() - new Date(birthDate)) / (1000 * 60 * 60 * 24);
            const ageYears = Math.max(0.1, ageInDays / 365.25);
            setCurrentAgeYears(ageYears);

            // --- STEP 1: AI Analysis to determine the text content for the tabs ---

            // Base prompt with all instructions
            const systemPrompt = "You are a pediatric health AI. Return ONLY valid JSON matching the schema. Be concise.";

            const userPrompt = 'Child: ' + gender + ', age ' + ageYears.toFixed(1) + ' years. Provide growthStats (targetHeightCm, targetWeightKg, heightPercentile, weightPercentile, bmiStatus, growthRate, bmi), vaccinations (string), nutrition (string), milestones (string).';

            // We must request structured JSON data for the tabs
            const structuredSchema = {
                type: "OBJECT",
                properties: {
                    "growthStats": {
                        type: "OBJECT",
                        properties: {
                            "targetHeightCm": { "type": "NUMBER" },
                            "targetWeightKg": { "type": "NUMBER" },
                            "heightPercentile": { "type": "STRING" },
                            "weightPercentile": { "type": "STRING" },
                            "bmiStatus": { "type": "STRING" },
                            "growthRate": { "type": "STRING" },
                            "bmi": { "type": "NUMBER" }
                        }
                    },
                    "vaccinations": { "type": "STRING" },
                    "nutrition": { "type": "STRING" },
                    "milestones": { "type": "STRING" }
                }
            };

            // FIX: Ensure callGeminiAPI receives generationConfig correctly
            const responseText = await callGeminiAPI(userPrompt, systemPrompt, null, 3, { responseMimeType: "application/json", responseSchema: structuredSchema });

            // Check for API error message before parsing JSON
            if (!responseText || responseText.startsWith("Error:")) {
                setError(responseText || "An error occurred during child health analysis.");
                return;
            }

            let parsedData;
            try {
                parsedData = JSON.parse(responseText);
            } catch (parseError) {
                console.error("JSON Parse Error:", parseError);
                console.error("Response text was:", responseText);
                setError("Failed to parse AI response. The AI returned invalid JSON. Please try again.");
                return;
            }

            // Validate parsed data structure
            if (!parsedData || !parsedData.growthStats || typeof parsedData.growthStats !== 'object') {
                console.error("Invalid response structure:", parsedData);
                setError("Received invalid response from AI. Please try again.");
                return;
            }

            // --- STEP 2: Process AI Data for UI ---
            const stats = parsedData.growthStats;

            setCurrentStats({
                ...stats,
                height: stats.targetHeightCm,
                weight: stats.targetWeightKg,
            });

            // Generate mock chart data based on target stats
            setChartData(generateChartData(ageYears, stats.targetHeightCm, stats.targetWeightKg));

            // Set the remaining AI analysis content
            setResult(parsedData);

            // LOG HISTORY
            const durationMs = Date.now() - startTime;
            logHistory(userId, 'Child Health', userPrompt, responseText, durationMs);

        } catch (err) {
            console.error("Child Health Analysis Error:", err);
            setError("An error occurred during child health analysis. Please ensure your API key is correct and services are enabled.");
        } finally {
            setLoading(false);
        }
    };

    // --- UI RENDER LOGIC ---

    // A simple function to render a mock line chart using basic HTML/CSS
    const MockLineChart = ({ data, xAxisLabel, yAxisLabel, dataKey, color }) => {
        const height = 200;
        const width = 300;

        if (!data || data.length < 2) return <p style={{ textAlign: 'center', color: '#999' }}>Not enough data for chart.</p>;

        const maxValue = Math.max(...data.map(d => d[dataKey]));
        const minValue = Math.min(...data.map(d => d[dataKey]));
        const range = Math.max(1, maxValue - minValue); // Ensure range is at least 1
        const normalizedData = data.map(d => ({
            ...d,
            y: height - (d[dataKey] - minValue) / range * height * 0.8 - 20, // Normalize and invert Y
            x: (d.age - data[0].age) / (data[data.length - 1].age - data[0].age) * width // Normalize X
        }));

        const points = normalizedData.map(d => `${d.x},${d.y}`).join(' ');

        return (
            <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #eee' }}>
                <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '10px' }}>{yAxisLabel}</div>
                <svg width={width + 50} height={height + 50} viewBox={'0 0 ' + (width + 50) + ' ' + (height + 50)}>
                    {/* Grid lines (simplified) */}
                    <line x1="25" y1="25" x2="25" y2={height + 25} stroke="#ccc" />
                    <line x1="25" y1={height + 25} x2={width + 25} y2={height + 25} stroke="#ccc" />

                    {/* Chart area */}
                    <g transform="translate(25, 25)">
                        <polyline fill="none" stroke={color} strokeWidth="3" points={points} />
                        {normalizedData.map((d, i) => (
                            <circle key={i} cx={d.x} cy={d.y} r="4" fill={color} />
                        ))}
                    </g>

                    {/* X-Axis Labels (Age) */}
                    {data.map((d, i) => (
                        <text key={'x' + i} x={25 + normalizedData[i].x} y={height + 45} fontSize="10" textAnchor="middle" fill="#666">{d.age}</text>
                    ))}
                    <text x={width / 2 + 25} y={height + 50} fontSize="12" fill="#333" textAnchor="middle">{xAxisLabel}</text>
                </svg>
            </div>
        );
    };

    const renderGrowthTracking = () => {
        if (!currentStats) return <p style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Run analysis to view growth data.</p>;

        const ageText = currentAgeYears < 2 ? `${Math.round(currentAgeYears * 12)} months` : `${currentAgeYears.toFixed(1)} years`;

        return (
            <div style={{ padding: '20px' }}>
                <h3 style={{ margin: '0 0 20px', color: '#333' }}>Growth Chart (Target/Ideal Growth)</h3>

                <div style={{ display: 'flex', gap: '20px', justifyContent: 'space-around' }}>
                    <MockLineChart
                        data={chartData}
                        dataKey="height"
                        xAxisLabel="Age (Years)"
                        yAxisLabel="Height (cm)"
                        color="#ff69b4"
                    />
                    <MockLineChart
                        data={chartData}
                        dataKey="weight"
                        xAxisLabel="Age (Years)"
                        yAxisLabel="Weight (kg)"
                        color="#7cfc00"
                    />
                </div>

                <h3 style={{ margin: '40px 0 20px', color: '#333', borderTop: '1px solid #eee', paddingTop: '20px' }}>Current Target Stats ({ageText})</h3>
                <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>

                    {/* Height */}
                    <div style={{ width: '20%', padding: '10px', backgroundColor: '#e9f0ff', borderRadius: '8px' }}>
                        <p style={{ fontSize: '2rem', color: '#007bff', fontWeight: 'bold', margin: '0 0 5px' }}>{currentStats.height} cm</p>
                        <p style={{ fontSize: '0.9rem', color: '#555', margin: 0 }}>Height</p>
                        <p style={{ fontSize: '0.8rem', color: '#007bff', margin: '5px 0 0' }}>{currentStats.heightPercentile}</p>
                    </div>

                    {/* Weight */}
                    <div style={{ width: '20%', padding: '10px', backgroundColor: '#e9f0ff', borderRadius: '8px' }}>
                        <p style={{ fontSize: '2rem', color: '#28a745', fontWeight: 'bold', margin: '0 0 5px' }}>{currentStats.weight} kg</p>
                        <p style={{ fontSize: '0.9rem', color: '#555', margin: 0 }}>Weight</p>
                        <p style={{ fontSize: '0.8rem', color: '#28a745', margin: '5px 0 0' }}>{currentStats.weightPercentile}</p>
                    </div>

                    {/* BMI */}
                    <div style={{ width: '20%', padding: '10px', backgroundColor: '#e9f0ff', borderRadius: '8px' }}>
                        <p style={{ fontSize: '2rem', color: '#ffc107', fontWeight: 'bold', margin: '0 0 5px' }}>{currentStats.bmi.toFixed(1)}</p>
                        <p style={{ fontSize: '0.9rem', color: '#555', margin: 0 }}>BMI</p>
                        <p style={{ fontSize: '0.8rem', color: '#ffc107', margin: '5px 0 0' }}>{currentStats.bmiStatus}</p>
                    </div>

                    {/* Growth Rate */}
                    <div style={{ width: '20%', padding: '10px', backgroundColor: '#e9f0ff', borderRadius: '8px' }}>
                        <p style={{ fontSize: '2rem', color: '#dc3545', fontWeight: 'bold', margin: '0 0 5px' }}>{currentStats.growthRate}</p>
                        <p style={{ fontSize: '0.9rem', color: '#555', margin: 0 }}>Growth Rate</p>
                        <p style={{ fontSize: '0.8rem', color: '#dc3545', margin: '5px 0 0' }}>Healthy</p>
                    </div>
                </div>
            </div>
        );
    };

    const renderContent = () => {
        if (!result) return <p style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Fill in the details and click "Analyze" to generate the health report.</p>;

        const key = activeTab;
        const titleMap = {
            'vaccinations': 'Required Immunizations',
            'nutrition': 'Targeted Nutritional Guidelines',
            'milestones': 'Developmental Milestones',
        };

        if (key === 'growth') {
            return renderGrowthTracking();
        }

        const content = result[key] || "No data available for this section.";

        return (
            <div style={{ padding: '20px' }}>
                <h3 style={{ color: '#007bff' }}>{titleMap[key]}</h3>
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, marginTop: '15px' }}>{content}</div>
            </div>
        );
    };


    return (
        <div style={{ maxWidth: '1000px', margin: '2rem auto', padding: '0 2rem' }}>
            <h2 style={{ textAlign: 'center', color: '#007bff' }}>Child Health & Development Analysis</h2>

            <StyledCard style={{ marginBottom: '1rem' }}>
                <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '2rem', alignItems: 'flex-end' }}>
                    <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', margin: '0 0 0.5rem', fontWeight: '600' }}>Child's Birth Date:</label>
                        <StyledInput
                            type="date"
                            value={birthDate}
                            onChange={(e) => setBirthDate(e.target.value)}
                            required
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', margin: '0 0 0.5rem', fontWeight: '600' }}>Gender:</label>
                        <select
                            value={gender}
                            onChange={(e) => setGender(e.target.value)}
                            style={inputStyles}
                            required
                        >
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                        </select>
                    </div>
                    <StyledButton type="submit" style={{ flex: '0 0 200px', padding: '12px 20px' }} disabled={loading}>
                        {loading ? 'Analyzing...' : 'Analyze Development'}
                    </StyledButton>
                </form>
                {error && <p style={{ color: '#dc3545', marginTop: '1rem', textAlign: 'center' }}>{error}</p>}
            </StyledCard>

            {/* Tab Navigation */}
            <div style={{ display: 'flex', borderBottom: '2px solid #ddd', marginBottom: '1rem' }}>
                {tabNames.map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        style={{
                            padding: '10px 20px',
                            border: 'none',
                            backgroundColor: 'transparent',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            fontWeight: activeTab === tab.key ? '700' : '400',
                            color: activeTab === tab.key ? '#007bff' : '#666',
                            borderBottom: activeTab === tab.key ? '2px solid #007bff' : '2px solid transparent',
                            marginBottom: '-2px', // Pull button slightly up over the border
                            transition: 'color 0.2s, border-bottom 0.2s',
                        }}
                    >
                        {tab.name}
                    </button>
                ))}
            </div>

            {/* Content Area */}
            <StyledCard style={{ minHeight: '400px', padding: 0 }}>
                {loading ? <LoadingSpinner /> : renderContent()}
            </StyledCard>
        </div>
    );
};


// --- SYMPTOM CHECKER COMPONENT (MODIFIED AND FIXED) ---

const commonSymptomChips = ["Fever", "Cough", "Runny nose", "Sore throat", "Nausea", "Vomiting", "Diarrhea", "Headache", "Body aches", "Fatigue"];

const SymptomChecker = () => {
    const { userId } = useContext(AuthContext);
    const [selectedSymptoms, setSelectedSymptoms] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]); // Array of possible conditions
    const [userProblemExplanation, setUserProblemExplanation] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const toggleSymptom = (symptom) => {
        if (selectedSymptoms.includes(symptom)) {
            setSelectedSymptoms(selectedSymptoms.filter(s => s !== symptom));
        } else {
            setSelectedSymptoms([...selectedSymptoms, symptom]);
        }
        setSearchTerm(''); // Clear search after selection
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!userId || selectedSymptoms.length === 0) {
            setError("Please log in and select at least one symptom.");
            return;
        }

        setLoading(true);
        setResults([]);
        setUserProblemExplanation('');
        setError(null);
        const startTime = Date.now();
        const symptomsString = selectedSymptoms.join(', ');

        try {
            // Step 1: Request structured JSON data for conditions
            const systemPrompt = "You are a medical symptom analyzer. Return ONLY valid JSON matching the schema. Be concise.";

            const userPrompt = 'Symptoms: ' + symptomsString + '. Provide problemExplanation (2 sentences) and 3 possibleConditions with matchPercentage, description, matchingSymptoms, and recommendations.';

            const structuredSchema = {
                type: "OBJECT",
                properties: {
                    "problemExplanation": { "type": "STRING" },
                    "possibleConditions": {
                        type: "ARRAY",
                        items: {
                            type: "OBJECT",
                            properties: {
                                "name": { "type": "STRING" },
                                "matchPercentage": { "type": "NUMBER" },
                                "description": { "type": "STRING" },
                                "matchingSymptoms": {
                                    type: "ARRAY",
                                    items: { "type": "STRING" }
                                },
                                "recommendations": {
                                    type: "ARRAY",
                                    items: { "type": "STRING" }
                                }
                            }
                        }
                    }
                }
            };

            const responseText = await callGeminiAPI(userPrompt, systemPrompt, null, 3, { responseMimeType: "application/json", responseSchema: structuredSchema });

            // Check for API error message before parsing JSON
            if (!responseText || responseText.startsWith("Error:")) {
                setError(responseText || "An error occurred during symptom analysis.");
                return;
            }

            let parsedData;
            try {
                parsedData = JSON.parse(responseText);
            } catch (parseError) {
                console.error("JSON Parse Error:", parseError);
                console.error("Response text was:", responseText);
                setError("Failed to parse AI response. The AI returned invalid JSON. Please try again.");
                return;
            }

            // Validate parsed data structure
            if (!parsedData || !parsedData.possibleConditions || !Array.isArray(parsedData.possibleConditions)) {
                console.error("Invalid response structure:", parsedData);
                setError("Received invalid response from AI. Missing expected data fields. Please try again.");
                return;
            }

            setUserProblemExplanation(parsedData.problemExplanation || "");
            setResults(parsedData.possibleConditions);

            // LOG HISTORY
            const durationMs = Date.now() - startTime;
            logHistory(userId, 'Symptom Checker', userPrompt, responseText, durationMs);

        } catch (err) {
            console.error("Symptom Checker Error:", err);
            setError("An error occurred during symptom analysis. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // FIX: Safely access symptoms using optional chaining and default empty array
    const allSymptomNames = Object.values(diseaseData)
        .flatMap(d => d.symp || []) // Use d.symp or empty array if undefined
        .filter((value, index, self) => self.indexOf(value) === index); // Unique list

    const filteredSuggestions = allSymptomNames
        .filter(s => s.toLowerCase().includes(searchTerm.toLowerCase()))
        .slice(0, 5); // Limit suggestions

    return (
        <div style={{ maxWidth: '800px', margin: '2rem auto', padding: '0 2rem' }}>
            <h2 style={{ textAlign: 'center', color: '#007bff' }}>AI Symptom Checker</h2>
            <p style={{ textAlign: 'center', color: '#555', marginBottom: '1.5rem' }}>
                Select your symptoms and get AI-powered health insights. Our system analyzes your symptoms to suggest possible conditions and next steps.
            </p>
            <StyledCard>
                {/* SELECTED SYMPTOMS */}
                <h4 style={{ margin: '0 0 10px', color: '#333' }}>Selected Symptoms ({selectedSymptoms.length})</h4>
                <div style={{ marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {selectedSymptoms.map(s => (
                        <div key={s} style={{
                            backgroundColor: '#e6f0ff', color: '#007bff', padding: '6px 10px', borderRadius: '4px', fontSize: '0.9rem', cursor: 'pointer', display: 'flex', alignItems: 'center'
                        }} onClick={() => toggleSymptom(s)}>
                            {s} <span style={{ marginLeft: '5px', fontWeight: 'bold' }}>&times;</span>
                        </div>
                    ))}
                </div>

                {/* ADD SYMPTOMS INPUT */}
                <div style={{ position: 'relative', marginBottom: '15px' }}>
                    <StyledInput
                        type="text"
                        placeholder="Search symptoms..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ paddingLeft: '40px', margin: 0, backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'%23888\'%3E%3Cpath d=\'M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5C16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundSize: '18px', backgroundPosition: '15px center' }}
                    />
                    {searchTerm && filteredSuggestions.length > 0 && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '6px', zIndex: 10 }}>
                            {filteredSuggestions.map(s => (
                                <div key={s} onClick={() => toggleSymptom(s)} style={{ padding: '8px 15px', cursor: 'pointer', borderBottom: '1px solid #eee', ':hover': { backgroundColor: '#f5f5f5' } }}>
                                    {s}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* COMMON SYMPTOM CHIPS */}
                <h4 style={{ margin: '0 0 10px', color: '#333' }}>Common Symptoms</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
                    {commonSymptomChips.filter(s => !selectedSymptoms.includes(s)).map(s => (
                        <div key={s} onClick={() => toggleSymptom(s)} style={{
                            backgroundColor: '#f8f9fa', color: '#6c757d', padding: '6px 10px', borderRadius: '20px', fontSize: '0.85rem', border: '1px solid #ced4da', cursor: 'pointer', transition: 'background-color 0.2s'
                        }}>
                            + {s}
                        </div>
                    ))}
                </div>

                <StyledButton onClick={handleSubmit} style={{ width: '100%', marginTop: '1rem' }} disabled={loading || selectedSymptoms.length === 0}>
                    {loading ? 'Analyzing...' : 'Analyze Symptoms'}
                </StyledButton>
            </StyledCard>

            {loading && <LoadingSpinner />}

            {error && <p style={{ color: '#dc3545', marginTop: '1rem', textAlign: 'center' }}>{error}</p>}

            {/* PROBLEM EXPLANATION */}
            {userProblemExplanation && (
                <StyledCard style={{ borderLeft: '4px solid #007bff', padding: '1.5rem 2rem', marginBottom: '1.5rem' }}>
                    <h3 style={{ margin: '0 0 10px', color: '#007bff' }}>Your Problem Explained:</h3>
                    <p style={{ margin: 0, lineHeight: 1.5 }}>{userProblemExplanation}</p>
                </StyledCard>
            )}

            {/* POSSIBLE CONDITIONS RESULTS */}
            {results && results.length > 0 && (
                <div style={{ marginTop: '2rem' }}>
                    <h3 style={{ color: '#333', marginBottom: '1rem' }}>Possible Conditions</h3>
                    {results.map((condition, index) => (
                        <StyledCard key={index} style={{
                            borderLeft: '4px solid ' + (condition.matchPercentage > 70 ? '#28a745' : '#ffc107'),
                            padding: '1.5rem 2rem',
                            marginBottom: '1rem'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h4 style={{ margin: 0, color: '#333' }}>{condition.name}</h4>
                                <span style={{
                                    fontSize: '1.2rem',
                                    fontWeight: 'bold',
                                    color: condition.matchPercentage > 70 ? '#28a745' : '#ffc107'
                                }}>
                                    {condition.matchPercentage}%
                                </span>
                            </div>
                            <p style={{ margin: '5px 0 10px', fontSize: '0.9rem', color: '#666' }}>{condition.description}</p>

                            <p style={{ fontSize: '0.9rem', fontWeight: '600', color: '#333', marginTop: '10px', marginBottom: '5px' }}>Matching Symptoms:</p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {condition.matchingSymptoms && condition.matchingSymptoms.map(s => (
                                    <span key={s} style={{ backgroundColor: '#e9f0ff', color: '#007bff', padding: '4px 8px', borderRadius: '4px', fontSize: '0.8rem' }}>{s}</span>
                                ))}
                            </div>

                            <p style={{ fontSize: '0.9rem', fontWeight: '600', color: '#333', marginTop: '15px', marginBottom: '5px' }}>Recommendations:</p>
                            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.9rem', color: '#666' }}>
                                {condition.recommendations && condition.recommendations.map((r, i) => <li key={i}>{r}</li>)}
                            </ul>
                        </StyledCard>
                    ))}

                    <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffeeba', color: '#856404', padding: '15px', borderRadius: '8px', fontSize: '0.9rem', marginTop: '20px' }}>
                        **Important:** This symptom checker is for informational purposes only and cannot replace professional medical advice. Always consult a healthcare provider for proper diagnosis and treatment.
                    </div>
                </div>
            )}
        </div>
    );
};


// --- DISEASE LIBRARY COMPONENT (MODIFIED) ---

const DiseaseLibrary = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRisk, setFilterRisk] = useState('All Diseases');
    const [selectedDisease, setSelectedDisease] = useState(null);

    const filteredDiseases = Object.values(diseaseData)
        .filter(d => {
            const matchesSearch = d.name.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesRisk = filterRisk === 'All Diseases' || d.risk === filterRisk;
            return matchesSearch && matchesRisk;
        })
        .sort((a, b) => a.name.localeCompare(b.name));

    const riskColor = (risk) => {
        switch (risk) {
            case 'High': return '#dc3545';
            case 'Medium': return '#ffc107';
            case 'Low': return '#28a745';
            default: return '#6c757d';
        }
    };

    const renderRiskIcon = (risk) => {
        const color = riskColor(risk);
        return (
            <span style={{ color: color, fontSize: '1.2rem', marginRight: '5px' }}>
                {risk === 'High' ? '⚠️' : risk === 'Medium' ? '🟡' : '✅'}
            </span>
        );
    };

    const renderDiseaseCard = (d) => (
        <StyledCard key={d.name} style={{ width: '300px', cursor: 'pointer', padding: '1.5rem', transition: 'box-shadow 0.3s' }} onClick={(e) => { e.preventDefault(); setSelectedDisease(d); }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <h4 style={{ margin: '0 0 5px', color: '#007bff' }}>{d.name}</h4>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: riskColor(d.risk) + '15',
                    color: riskColor(d.risk),
                    padding: '3px 8px',
                    borderRadius: '4px',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                }}>
                    {renderRiskIcon(d.risk)} {d.risk}
                </div>
            </div>
            <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '10px' }}>{d.desc.substring(0, 70)}...</p>

            <p style={{ fontSize: '0.8rem', fontWeight: '600', color: '#333' }}>Common Symptoms:</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                {d.symp.slice(0, 3).map(s => (
                    <span key={s} style={{ backgroundColor: '#eee', color: '#555', padding: '2px 6px', borderRadius: '4px', fontSize: '0.75rem' }}>{s}</span>
                ))}
                {d.symp.length > 3 && <span style={{ backgroundColor: '#eee', color: '#555', padding: '2px 6px', borderRadius: '4px', fontSize: '0.75rem' }}>+{d.symp.length - 3} more</span>}
            </div>

            <a href="#" onClick={(e) => { e.preventDefault(); setSelectedDisease(d); }} style={{ color: '#007bff', fontSize: '0.9rem', display: 'block', marginTop: '15px', textDecoration: 'none' }}>
                Learn more &rarr;
            </a>
        </StyledCard>
    );

    const renderDiseaseDetails = (d) => (
        <div style={{ maxWidth: '800px', margin: '2rem auto', padding: '0 2rem' }}>
            <a href="#" onClick={(e) => { e.preventDefault(); setSelectedDisease(null); }} style={{ color: '#007bff', textDecoration: 'none', marginBottom: '20px', display: 'inline-block' }}>
                &larr; Back to Disease Library
            </a>
            <StyledCard style={{ padding: '2.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #eee', paddingBottom: '1rem' }}>
                    <div>
                        <h2 style={{ margin: '0 0 5px', color: '#333' }}>{d.name}</h2>
                        <p style={{ margin: 0, color: '#666', fontSize: '1rem' }}>{d.desc}</p>
                    </div>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: riskColor(d.risk) + '15',
                        color: riskColor(d.risk),
                        padding: '8px 15px',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        fontWeight: 'bold'
                    }}>
                        {renderRiskIcon(d.risk)} {d.risk}
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem 4rem', marginTop: '2rem' }}>
                    {/* Symptoms */}
                    <div>
                        <h4 style={{ color: '#007bff', margin: '0 0 10px' }}><span style={{ marginRight: '8px' }}>🚨</span> Symptoms</h4>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {d.symp.map(s => <li key={s} style={{ marginBottom: '5px', color: '#333', fontSize: '0.95rem' }}>• {s}</li>)}
                        </ul>
                    </div>
                    {/* Prevention */}
                    <div>
                        <h4 style={{ color: '#28a745', margin: '0 0 10px' }}><span style={{ marginRight: '8px' }}>🛡️</span> Prevention</h4>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {d.prev.map(p => <li key={p} style={{ marginBottom: '5px', color: '#333', fontSize: '0.95rem' }}>• {p}</li>)}
                        </ul>
                    </div>
                    {/* Causes */}
                    <div>
                        <h4 style={{ color: '#ffc107', margin: '0 0 10px' }}><span style={{ marginRight: '8px' }}>🧬</span> Causes</h4>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {d.cause.map(c => <li key={c} style={{ marginBottom: '5px', color: '#333', fontSize: '0.95rem' }}>• {c}</li>)}
                        </ul>
                    </div>
                    {/* Treatment */}
                    <div>
                        <h4 style={{ color: '#dc3545', margin: '0 0 10px' }}><span style={{ marginRight: '8px' }}>❤️</span> Treatment</h4>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {d.treat.map(t => <li key={t} style={{ marginBottom: '5px', color: '#333', fontSize: '0.95rem' }}>• {t}</li>)}
                        </ul>
                    </div>
                </div>

                {/* When to see a doctor */}
                <div style={{ backgroundColor: '#fffbe3', border: '1px solid #ffe082', padding: '15px', borderRadius: '8px', marginTop: '2rem' }}>
                    <p style={{ fontWeight: '600', margin: '0 0 5px', color: '#333' }}>When to See a Doctor</p>
                    <p style={{ margin: 0, fontSize: '0.9rem', color: '#555' }}>If symptoms persist for more than 10 days or worsen significantly.</p>
                </div>

                {/* Disclaimer */}
                <div style={{ backgroundColor: '#e9f0ff', border: '1px solid #cce5ff', padding: '15px', borderRadius: '8px', marginTop: '1rem' }}>
                    <p style={{ fontWeight: '600', margin: '0 0 5px', color: '#007bff' }}>Disclaimer:</p>
                    <p style={{ margin: 0, fontSize: '0.9rem', color: '#0056b3' }}>This information is for educational purposes only and should not replace professional medical advice. Always consult with a healthcare provider for proper diagnosis and treatment.</p>
                </div>

            </StyledCard>
        </div>
    );

    if (selectedDisease) {
        return renderDiseaseDetails(selectedDisease);
    }

    return (
        <div style={{ maxWidth: '1200px', margin: '2rem auto', padding: '0 2rem' }}>
            <h2 style={{ textAlign: 'center', color: '#007bff' }}>Disease Library</h2>
            <p style={{ textAlign: 'center', color: '#555', marginBottom: '1.5rem' }}>
                Comprehensive information about common diseases, their symptoms, causes, prevention strategies, and treatment options.
            </p>
            <StyledCard style={{ padding: '1.5rem', marginBottom: '2rem' }}>
                <div style={{ display: 'flex', gap: '1rem' }}>
                    <div style={{ position: 'relative', flexGrow: 1 }}>
                        <StyledInput
                            type="text"
                            placeholder="Search diseases..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ paddingLeft: '40px', margin: 0, backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'%23888\'%3E%3Cpath d=\'M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5C16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundSize: '18px', backgroundPosition: '15px center' }}
                        />
                    </div>
                    <select
                        value={filterRisk}
                        onChange={(e) => setFilterRisk(e.target.value)}
                        style={{ ...inputStyles, flexGrow: 0, width: '150px', margin: 0 }}
                    >
                        <option value="All Diseases">All Diseases</option>
                        <option value="Low">Low Risk</option>
                        <option value="Medium">Medium Risk</option>
                        <option value="High">High Risk</option>
                    </select>
                </div>
            </StyledCard>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'center' }}>
                {filteredDiseases.map(renderDiseaseCard)}
                {filteredDiseases.length === 0 && <p style={{ textAlign: 'center', color: '#999', padding: '4rem' }}>No diseases found matching your criteria.</p>}
            </div>
        </div>
    );
};


// --- REPORTS SECTION (REINCLUDED) ---

const ReportsSection = () => {
    // ... (ReportsSection content remains unchanged, uses existing Firebase and AI logic)
    const { userId } = useContext(AuthContext);
    const [reportFile1, setReportFile1] = useState(null); // Current/New Report
    const [reportFile2, setReportFile2] = useState(null); // Past/Old Report
    const [reports, setReports] = useState([]); // List of stored reports
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch reports on mount and when user changes
    useEffect(() => {
        if (!db || !userId) {
            setReports([]);
            return;
        }

        const reportsColRef = collection(db, getPrivateCollectionPath(userId, 'medicalReports'));
        const q = query(reportsColRef);

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedReports = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setReports(fetchedReports.sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt)));
        }, (err) => {
            console.error("Error fetching reports:", err);
        });

        return () => unsubscribe();
    }, [userId]);

    // Handle file upload to Firebase Storage and Firestore metadata
    const handleUpload = async (file) => {
        if (!userId || !file) return;

        setLoading(true);
        try {
            const fileId = Date.now().toString();
            const storagePath = getPrivateStoragePath(userId, fileId, file.name);
            const storageRef = ref(storage, storagePath);

            // 1. Upload file
            await uploadBytes(storageRef, file);

            // 2. Get download URL
            const url = await getDownloadURL(storageRef);

            // 3. Save metadata to Firestore
            const reportsColRef = collection(db, getPrivateCollectionPath(userId, 'medicalReports'));
            await setDoc(doc(reportsColRef, fileId), {
                fileName: file.name,
                fileType: file.type,
                downloadURL: url,
                storagePath: storagePath,
                uploadedAt: new Date().toISOString()
            });

        } catch (e) {
            setError("File upload failed. Check file size/type.");
            console.error("Upload Error:", e);
        } finally {
            setLoading(false);
        }
    };

    // Handle report comparison (AI Analysis)
    const handleAnalysis = async (e) => {
        e.preventDefault();
        if (!userId || !reportFile1) {
            setError("Please select at least the New/Current Report for analysis.");
            return;
        }

        setLoading(true);
        setResult(null);
        setError(null);
        const startTime = Date.now();

        try {
            const report1Data = await fileToBase64(reportFile1);
            let report2Data = null;

            let comparisonPrompt = 'Analyze the following medical report(s) and provide a summary of the patient\'s condition and any notable changes or findings.';

            // If a second, older report is provided, adjust the prompt for comparison
            if (reportFile2) {
                report2Data = await fileToBase64(reportFile2);
                comparisonPrompt = 'Compare the New/Current Report with the Past/Old Report. Summarize the patient\'s current condition based on the new data, highlighting any progression, improvement, or critical changes observed since the past report.';
            }

            // Gemini API requires an array of parts for multimodal
            const parts = [
                { text: comparisonPrompt },
                { inlineData: report1Data },
                ...(report2Data ? [{ inlineData: report2Data }] : [])
            ];

            // Updated system prompt for conversational and clean output
            const systemPrompt = "You are a Medical Report Analysis AI. Provide a clear, conversational explanation of the reports' findings (lab values, diagnosis, medications) and an overall summary of the patient's condition. If comparing two reports, focus on the differences and trajectory of the illness. Use simple Markdown lists for clarity, but avoid rigid tables or complex formatting.";

            // FIX: Using string concatenation instead of template literals
            const url = API_URL_BASE + API_MODEL + ':generateContent?key=' + apiKey;

            const payload = {
                contents: [{ role: "user", parts: parts }],
                systemInstruction: { parts: [{ text: systemPrompt }] }
            };

            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const resultJson = await response.json();
            const aiResponse = resultJson.candidates?.[0]?.content?.parts?.[0]?.text || "AI response failed to parse.";
            setResult(aiResponse);

            // LOG HISTORY
            const durationMs = Date.now() - startTime;
            logHistory(userId, 'Reports Analysis', comparisonPrompt, aiResponse, durationMs);

        } catch (err) {
            console.error("Report Analysis Error:", err);
            setError("An error occurred during report analysis. Please ensure reports are PDFs.");
        } finally {
            setLoading(false);
        }
    };

    // Handle report deletion from Storage and Firestore
    const handleDelete = async (report) => {
        if (!userId || !report) return;

        // Using a custom confirmation message as alert()/confirm() is forbidden
        const isConfirmed = window.prompt('Type \'DELETE\' to confirm deletion of ' + report.fileName + ':');

        if (isConfirmed === 'DELETE') {
            try {
                // 1. Delete from Storage
                const storageRef = ref(storage, report.storagePath);
                await deleteObject(storageRef);

                // 2. Delete metadata from Firestore
                const docRef = doc(db, getPrivateCollectionPath(userId, 'medicalReports'), report.id);
                await deleteDoc(docRef);

                // Using a custom alert message
                window.alert(report.fileName + ' successfully deleted.');
            } catch (e) {
                console.error("Deletion Error:", e);
                window.alert("Failed to delete report.");
            }
        } else if (isConfirmed !== null) {
            window.alert("Deletion cancelled or confirmation text was incorrect.");
        }
    };


    return (
        <div style={{ maxWidth: '1000px', margin: '2rem auto', padding: '0 2rem' }}>
            <h2 style={{ textAlign: 'center', color: '#007bff' }}>Medical Reports Section</h2>
            <p style={{ textAlign: 'center', color: '#555', marginBottom: '1.5rem' }}>
                Upload your medical reports (PDF only) for storage and use the analysis feature to compare them over time.
            </p>

            {/* UPLOAD SECTION */}
            <StyledCard>
                <h3 style={{ borderBottom: '1px solid #eee', paddingBottom: '1rem', marginBottom: '1.5rem' }}>Upload New Report</h3>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <StyledInput
                        type="file"
                        accept=".pdf"
                        onChange={(e) => setReportFile1(e.target.files[0])}
                        style={{ flexGrow: 1 }}
                    />
                    <StyledButton onClick={() => handleUpload(reportFile1)} disabled={loading || !reportFile1} style={{ backgroundColor: '#28a745' }}>
                        {loading ? 'Uploading...' : 'Upload Report'}
                    </StyledButton>
                </div>
                {error && <p style={{ color: '#dc3545', marginTop: '1rem' }}>{error}</p>}
            </StyledCard>


            {/* COMPARISON ANALYSIS SECTION */}
            <StyledCard>
                <h3 style={{ borderBottom: '1px solid #eee', paddingBottom: '1rem', marginBottom: '1.5rem' }}>AI Comparative Analysis</h3>
                <form onSubmit={handleAnalysis}>
                    <label style={{ display: 'block', margin: '1rem 0 0.5rem', fontWeight: '600' }}>1. New/Current Report (Required):</label>
                    <StyledInput
                        type="file"
                        accept=".pdf"
                        onChange={(e) => setReportFile1(e.target.files[0])}
                        required
                    />

                    <label style={{ display: 'block', margin: '1rem 0 0.5rem', fontWeight: '600' }}>2. Past/Old Report (Optional for comparison):</label>
                    <StyledInput
                        type="file"
                        accept=".pdf"
                        onChange={(e) => setReportFile2(e.target.files[0])}
                    />

                    <StyledButton type="submit" style={{ width: '100%', marginTop: '2rem' }} disabled={loading || !reportFile1}>
                        {loading ? 'Analyzing...' : 'Analyze Reports'}
                    </StyledButton>
                </form>
            </StyledCard>

            {/* RESULTS */}
            {loading && <LoadingSpinner />}
            {result && (
                <StyledCard style={{ borderLeft: '4px solid #007bff' }}>
                    <h3 style={{ color: '#007bff' }}>HealthAI Report Analysis:</h3>
                    <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{result}</div>
                </StyledCard>
            )}

            {/* STORED REPORTS LIST */}
            <StyledCard>
                <h3 style={{ borderBottom: '1px solid #eee', paddingBottom: '1rem', marginBottom: '1.5rem' }}>Your Stored Reports ({reports.length})</h3>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                    {reports.map(report => (
                        <li key={report.id} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '10px 0', borderBottom: '1px dotted #eee'
                        }}>
                            <div style={{ fontSize: '0.9rem' }}>
                                <span style={{ fontWeight: '600' }}>{report.fileName}</span>
                                <span style={{ marginLeft: '10px', color: '#999' }}>({new Date(report.uploadedAt).toLocaleDateString()})</span>
                            </div>
                            <div>
                                <a href={report.downloadURL} target="_blank" rel="noopener noreferrer" style={{ ...navLinkStyles(false), color: '#17a2b8', padding: '5px 10px', border: '1px solid #17a2b8' }}>View</a>
                                {/* Added inline styling for the custom prompt button */}
                                <StyledButton onClick={() => handleDelete(report)} style={{ backgroundColor: '#dc3545', padding: '5px 10px', marginLeft: '10px', fontSize: '0.8rem' }}>Delete</StyledButton>
                            </div>
                        </li>
                    ))}
                    {reports.length === 0 && <p style={{ color: '#999', textAlign: 'center' }}>No reports uploaded yet.</p>}
                </ul>
            </StyledCard>
        </div>
    );
};


// --- CHAT BOT COMPONENT ---

const chatbotContainerStyles = {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    zIndex: 1000,
    width: '350px',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.25)',
    borderRadius: '12px',
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
};

const chatbotToggleStyles = {
    ...buttonPrimary,
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    padding: 0,
    fontSize: '1.5rem',
    position: 'absolute',
    bottom: 0,
    right: 0,
};

const ChatBot = () => {
    const { userId } = useContext(AuthContext);
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([{ sender: 'ai', text: 'Hello! I am HealthAI Bot. I can answer questions about this app or give you general health information. How can I help?' }]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSend = async (e) => {
        e.preventDefault();
        if (!input.trim() || loading) return;

        const userMessage = input;
        setInput('');
        setMessages(prev => [...prev, { sender: 'user', text: userMessage }]);
        setLoading(true);
        const startTime = Date.now();

        // Updated system prompt for conversational and clean output
        const systemPrompt = "You are HealthAI ChatBot, a helpful and responsible medical and application assistant. You must answer questions about the features of the HealthAI application (Disease Prediction, Symptom Checker, Child Health, Disease Library, Reports, and Authentication) or provide general, well-known health and wellness information. Always emphasize the need to consult a real medical professional for personal diagnosis or treatment. Keep responses conversational and use simple Markdown formatting.";

        try {
            const aiResponse = await callGeminiAPI(userMessage, systemPrompt);
            setMessages(prev => [...prev, { sender: 'ai', text: aiResponse }]);

            // LOG HISTORY
            const durationMs = Date.now() - startTime;
            logHistory(userId, 'ChatBot', userMessage, aiResponse, durationMs);
        } catch (error) {
            setMessages(prev => [...prev, { sender: 'ai', text: "Sorry, I ran into an error. Please try again or check your API key setup." }]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={isOpen ? chatbotContainerStyles : { ...chatbotToggleStyles, position: 'fixed', bottom: '20px', right: '20px' }}>
            {!isOpen ? (
                <button
                    style={chatbotToggleStyles}
                    onClick={() => setIsOpen(true)}
                >
                    💬
                </button>
            ) : (
                <div style={{ height: '500px', display: 'flex', flexDirection: 'column' }}>
                    <header style={{ padding: '10px 15px', backgroundColor: '#007bff', color: 'white', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h4 style={{ margin: 0 }}>HealthAI ChatBot</h4>
                        <button onClick={() => setIsOpen(false)} style={{ background: 'none', border: 'none', color: 'white', fontSize: '1.2rem', cursor: 'pointer' }}>
                            &times;
                        </button>
                    </header>
                    <div style={{ flexGrow: 1, padding: '15px', overflowY: 'auto', backgroundColor: '#f9f9f9' }}>
                        {messages.map((msg, index) => (
                            <div key={index} style={{
                                display: 'flex',
                                justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                                marginBottom: '10px'
                            }}>
                                <div style={{
                                    maxWidth: '80%',
                                    padding: '10px',
                                    borderRadius: '10px',
                                    fontSize: '0.9rem',
                                    backgroundColor: msg.sender === 'user' ? '#e6f0ff' : 'white',
                                    color: '#333',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                    whiteSpace: 'pre-wrap'
                                }}>
                                    {msg.text}
                                </div>
                            </div>
                        ))}
                        {loading && <LoadingSpinner />}
                    </div>
                    <form onSubmit={handleSend} style={{ borderTop: '1px solid #eee', padding: '10px 15px', display: 'flex', gap: '10px' }}>
                        <StyledInput
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Ask a health or app question..."
                            style={{ margin: 0, flexGrow: 1, padding: '8px' }}
                            disabled={loading}
                        />
                        <StyledButton type="submit" style={{ padding: '8px 15px', fontSize: '0.9rem' }} disabled={loading}>
                            Send
                        </StyledButton>
                    </form>
                </div>
            )}
        </div>
    );
};


// --- MAIN APP COMPONENT ---

const App = () => {
    const [currentPage, setCurrentPage] = useState('home');
    const { isAuthReady } = useContext(AuthContext);

    // Dynamic component rendering based on current page
    let ContentComponent;
    if (!isAuthReady) {
        ContentComponent = LoadingSpinner;
    } else if (currentPage === 'login' || currentPage === 'register') {
        ContentComponent = () => <AuthPage type={currentPage} setCurrentPage={setCurrentPage} />;
    } else {
        switch (currentPage) {
            case 'home':
                ContentComponent = () => <HomePage setCurrentPage={setCurrentPage} />;
                break;
            case 'predict':
                ContentComponent = DiseasePrediction;
                break;
            case 'symptoms':
                ContentComponent = SymptomChecker;
                break;
            case 'child':
                ContentComponent = ChildHealth;
                break;
            case 'library':
                ContentComponent = DiseaseLibrary;
                break;
            case 'reports':
                ContentComponent = ReportsSection;
                break;
            default:
                ContentComponent = () => <HomePage setCurrentPage={setCurrentPage} />;
        }
    }


    return (
        <div style={baseStyles}>
            <Header currentPage={currentPage} setCurrentPage={setCurrentPage} />
            <main style={{ flexGrow: 1, padding: '2rem 0' }}>
                <ContentComponent />
            </main>
            <footer style={{ textAlign: 'center', padding: '1rem', borderTop: '1px solid #eee', color: '#999' }}>
                &copy; 2024 HealthAI. AI-Powered Healthcare System.
            </footer>

            {/* HealthAI ChatBot integrated here */}
            <ChatBot />

        </div>
    );
};

// FIX: Ensure App component is wrapped in AuthProvider and exported as DEFAULT
export default () => (
    <AuthProvider>
        <App />
    </AuthProvider>
);